import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Hls from 'hls.js'
import { useHlsInstance } from '@/hooks/useHlsInstance'
import { useHlsMux } from '@/hooks/useHlsMux'
import { useLatestRef } from '@/hooks/useLatestRef'
import { useAngelPlayerAPI, useAngelPlayerContext } from '@/organisms/AngelPlayer/AngelPlayerContext/AngelPlayerContext'
import { HlsErrorOverlay } from '@/organisms/HlsPlayer/HlsErrorOverlay'
import { HlsPlayerUI } from '@/organisms/HlsPlayer/HlsPlayerUI'
import { HlsAdOverlay } from '@/organisms/HlsPlayer/ads/HlsAdOverlay'
import { useAdSchedule } from '@/organisms/HlsPlayer/ads/useAdSchedule'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import type { Asset, PlayerOverlay, UpNextConfig } from '../BitmovinPlayer/types'
import { HlsPlayerController } from './HlsPlayerController'
import {
  createAngelPlayerEventShim,
  createAudioChangedShim,
  createPlayerEventShim,
  createSubtitleDisabledShim,
  createSubtitleEnabledShim,
  createTimeChangedShim,
  createUserInteractionShim,
} from './hlsEventShims'

const TIME_CHANGED_INTERVAL_MS = 250

interface HlsPlayerProps {
  asset: Asset
  autoplay?: boolean
  aspectClassName?: string
  overlays?: PlayerOverlay[]
  isMuted?: boolean
  introStartTime?: number | null
  introEndTime?: number | null
  episodeGuid?: string
  upNext?: UpNextConfig
  showContentShareButton?: boolean
  onShare?: () => void
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  onOpenParentalControls?: () => void
  enableAds?: boolean
  onAdStarted?: () => void
  onAdFinished?: () => void
  onAdSkipped?: () => void
  anonymousUserId?: string
  loggedInUserId?: string | null
  sessionId?: string
}

/**
 * HLS.js-based video player component.
 *
 * Renders an HTML5 video element with HLS.js for adaptive streaming,
 * overlaid with custom playback controls (HlsPlayerUI).
 *
 * Registers with AngelPlayerContext so external consumers (SayThankYouModal,
 * VideoReactionButton, etc.) can access the controller, and context-managed
 * features (analytics, watch position, keyboard shortcuts, track restoration)
 * work automatically.
 */
export const HlsPlayer: React.FC<HlsPlayerProps> = ({
  asset,
  autoplay,
  aspectClassName = 'w-full h-full',
  overlays,
  isMuted,
  introStartTime,
  introEndTime,
  episodeGuid,
  upNext,
  showContentShareButton,
  onShare,
  showParentalControlsButton,
  parentalControlsEnabled,
  parentalControlsApplied,
  parentalControlsTotal,
  onOpenParentalControls,
  enableAds,
  onAdStarted,
  onAdFinished,
  onAdSkipped,
  anonymousUserId,
  loggedInUserId,
  sessionId,
}) => {
  const router = useSafeRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef<HlsPlayerController | null>(null)
  const [controllerReady, setControllerReady] = useState(false)
  const [fatalError, setFatalError] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const loadedUrlRef = useRef<string | null>(null)

  const { hasPlayed } = useAngelPlayerContext()
  const contextAPI = useAngelPlayerAPI()
  // Store context callbacks in a ref so event handlers/intervals always call the latest
  // version. Context callbacks are recreated by useCallback when their deps change (e.g.,
  // isCreditsTracked, hasPlayed) — without a ref, the 250ms interval and video event
  // handlers would capture stale closures and fire duplicate tracking events.
  const contextRef = useLatestRef(contextAPI)
  const hasPlayedRef = useLatestRef(hasPlayed)

  const { adState, currentAd, isContentBlocked, onAdVideoEnded, onAdVideoSkipped } = useAdSchedule({
    vmapUrl: asset.vmapUrl,
    enableAds: enableAds ?? false,
    videoRef,
    onAdStarted,
    onAdFinished,
    onAdSkipped,
  })

  const handleFatalError = (message: string) => {
    setFatalError(message)
  }

  const { hlsRef, isReady } = useHlsInstance({
    videoRef,
    url: asset.sourceConfig.hls,
    muted: isMuted,
    onFatalError: handleFatalError,
  })

  useHlsMux({
    videoRef,
    hlsRef,
    isReady,
    asset,
    autoplay,
    anonymousUserId,
    loggedInUserId,
    sessionId,
  })

  // Initialize controller when HLS.js source is ready and register with AngelPlayerContext.
  // HlsPlayerController.load() is idempotent — when CollectiblesPlayer's loadAndPlayAsset
  // effect fires (triggered by setController), it calls controller.load() but the URL matches
  // the already-loaded source, so no reload occurs.
  useEffect(() => {
    if (!isReady) return

    const video = videoRef.current
    const hls = hlsRef.current
    if (!video || !hls) return

    const controller = new HlsPlayerController(video, hls, asset, autoplay && !isContentBlocked)
    controllerRef.current = controller
    loadedUrlRef.current = asset.sourceConfig.hls
    setControllerReady(true)

    contextRef.current.setController?.(controller)
    contextRef.current.setIsSourceLoaded?.(true)
    contextRef.current.onSourceLoaded?.(createPlayerEventShim())
    contextRef.current.onReady?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when isReady becomes true
  }, [isReady])

  // Wire video element events to AngelPlayerContext callbacks.
  // These fire analytics tracking, update playing/playingProgress state for external
  // consumers, and trigger watch position saves.
  useEffect(() => {
    if (!controllerReady) return
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => contextRef.current.onPlaying?.()
    const handlePause = () => {
      const time = video.currentTime
      contextRef.current.onPaused?.(
        createUserInteractionShim('ui'),
        createAngelPlayerEventShim(time, hasPlayedRef.current),
      )
    }
    const handleSeeking = () => contextRef.current.onSeek?.()
    const handleSeeked = () => {
      const time = video.currentTime
      contextRef.current.onSeeked?.(
        createUserInteractionShim('ui'),
        createAngelPlayerEventShim(time, hasPlayedRef.current),
      )
    }
    const handleEnded = () => contextRef.current.onPlaybackFinished?.()

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('seeking', handleSeeking)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('ended', handleEnded)

    // timeChanged interval — 250ms matching Bitmovin's frequency.
    // Drives playingProgress updates, intro detection, and credits tracking in context.
    const timeInterval = setInterval(() => {
      contextRef.current.onTimeChanged?.(createTimeChangedShim())
    }, TIME_CHANGED_INTERVAL_MS)

    // Watch position persistence on page leave or route change
    const handleSavePosition = () => contextRef.current.saveWatchPosition?.()
    window.addEventListener('beforeunload', handleSavePosition)
    router.events.on('routeChangeStart', handleSavePosition)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('seeking', handleSeeking)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('ended', handleEnded)
      clearInterval(timeInterval)
      window.removeEventListener('beforeunload', handleSavePosition)
      router.events.off('routeChangeStart', handleSavePosition)
    }
  }, [controllerReady, contextRef, hasPlayedRef, router.events])

  // Wire HLS.js audio/subtitle track change events to context for track preference persistence.
  useEffect(() => {
    if (!controllerReady) return
    const hls = hlsRef.current
    if (!hls) return

    const handleAudioSwitch = () => {
      const track = hls.audioTracks[hls.audioTrack]
      if (track?.lang) {
        contextRef.current.onAudioChanged?.(createAudioChangedShim(track.lang))
      }
    }

    const handleSubtitleSwitch = (_event: string, data: { id: number }) => {
      if (data.id >= 0) {
        const track = hls.subtitleTracks[data.id]
        if (track?.lang) {
          contextRef.current.onSubtitleEnabled?.(createSubtitleEnabledShim(track.lang))
        }
      } else {
        contextRef.current.onSubtitleDisabled?.(createSubtitleDisabledShim())
      }
    }

    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, handleAudioSwitch)
    hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleSwitch)

    return () => {
      hls.off(Hls.Events.AUDIO_TRACK_SWITCHED, handleAudioSwitch)
      hls.off(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleSwitch)
    }
  }, [controllerReady, contextRef, hlsRef])

  // Clean up on unmount: save watch position and clear context controller.
  // Uses contextRef.current directly (not a snapshot) so the cleanup always calls
  // the latest saveWatchPosition, which has the real controller in its closure.
  useEffect(() => {
    const ref = contextRef
    return () => {
      ref.current.saveWatchPosition?.()
      ref.current.setController?.(undefined)
    }
  }, [contextRef])

  // Handle asset changes (e.g., switching episodes).
  // Only reload if the streaming URL actually changed — the asset object reference
  // changes frequently (e.g., when nextContent arrives or useEpisodeAsset recomputes)
  // but the URL stays the same. Reloading on every reference change destroys buffered
  // data and makes the player unresponsive.
  useEffect(() => {
    const controller = controllerRef.current
    if (!controller) return
    const newUrl = asset.sourceConfig.hls
    if (newUrl === loadedUrlRef.current) return
    loadedUrlRef.current = newUrl

    controller.load({
      sourceConfig: asset.sourceConfig,
      name: asset.name,
      framesPerSecond: asset.frameRate,
      adjustmentFactor: asset.adjustmentFactor,
      framesDroppedAtFullMinute: asset.framesDroppedEachMinute,
      frameDuration: 1 / asset.frameRate,
      offsetToMidFrame: 0,
      position: asset.position,
    })
  }, [asset])

  const handleRetry = () => {
    retryCountRef.current += 1
    setFatalError(null)
    const hls = hlsRef.current
    if (hls) {
      hls.loadSource(asset.sourceConfig.hls)
    }
  }

  // Start content playback after pre-roll ad completes.
  // When isContentBlocked transitions from true to false and autoplay was requested,
  // the content video was suppressed — now trigger play.
  const wasBlockedRef = useRef(isContentBlocked)
  useEffect(() => {
    if (wasBlockedRef.current && !isContentBlocked && autoplay) {
      const video = videoRef.current
      if (video) {
        video.play().catch((error) => {
          if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')) return
          logger().warn('Content autoplay after pre-roll failed', {}, error)
        })
      }
    }
    wasBlockedRef.current = isContentBlocked
  }, [isContentBlocked, autoplay])

  const isAdPlaying = adState === 'playing' && currentAd !== null

  return (
    <div ref={containerRef} id="hlsjs-player" className={classNames([aspectClassName, 'relative bg-black'])}>
      <video ref={videoRef} className="h-full w-full object-contain" playsInline />
      {controllerReady && !fatalError && !isAdPlaying && (
        <HlsPlayerUI
          videoRef={videoRef}
          controller={controllerRef.current}
          containerRef={containerRef}
          asset={asset}
          introStartTime={introStartTime}
          introEndTime={introEndTime}
          episodeGuid={episodeGuid}
          upNext={upNext}
          showContentShareButton={showContentShareButton}
          onShare={onShare}
          showParentalControlsButton={showParentalControlsButton}
          parentalControlsEnabled={parentalControlsEnabled}
          parentalControlsApplied={parentalControlsApplied}
          parentalControlsTotal={parentalControlsTotal}
          onOpenParentalControls={onOpenParentalControls}
        />
      )}
      {isAdPlaying && currentAd && (
        <HlsAdOverlay ad={currentAd} onEnded={onAdVideoEnded} onSkipped={onAdVideoSkipped} />
      )}
      {fatalError && <HlsErrorOverlay message={fatalError} retryCount={retryCountRef.current} onRetry={handleRetry} />}
      {overlays?.map(({ key, Component }) => (
        <Component key={key} />
      ))}
    </div>
  )
}
